/**
 * Centralized Filter Configurations
 * 
 * This file contains all common filter configurations used across the application.
 * Instead of defining filter fields manually, you can just call filters by name.
 * 
 * Usage:
 * import { getFilterConfig } from "../configs/filterConfigs";
 * 
 * const filterFields = getFilterConfig(['department', 'status'], {
 *   departmentOptions: departmentList
 * });
 */

/**
 * Pre-defined filter configurations
 * Each filter has a default configuration that can be overridden
 */
export const FILTER_CONFIGS = {
  // Department Filter
  department: {
    name: "department_id",
    label: "Department",
    type: "select",
    placeholder: "All Departments",
    optionsKey: "departmentOptions", // Key to look for in options object
  },

  // Status Filter (Active/Inactive)
  status: {
    name: "status",
    label: "Status",
    type: "radio",
    options: [
      { label: "All", value: "" },
      { label: "Active", value: "1" },
      { label: "Inactive", value: "0" },
    ],
  },

  // Status Filter (Active/Inactive)
  collateral_applicable: {
    name: "collateral_applicable",
    label: "Loan Type",
    type: "select",
    placeholder: "All Loan Types",
    options: [
      { label: "All", value: "" },
      { label: "Unsecure", value: "0" },
      { label: "Secure", value: "1" }, 
    ],
  },

  // Status Select (Dropdown version)
  statusSelect: {
    name: "status",
    label: "Status",
    type: "select",
    placeholder: "All Status",
    options: [
      { label: "Active", value: "1" },
      { label: "Inactive", value: "0" },
    ],
  },

  // Task Status Filter
  taskStatus: {
    name: "status",
    label: "Status",
    type: "select",
    placeholder: "All Status",
    options: [
      { label: "All", value: "" },
      { label: "Pending", value: "0" },
      { label: "In Progress", value: "1" },
      { label: "In Review", value: "2" },
      { label: "Completed", value: "3" },
      { label: "Cancelled", value: "4" },
    ],
  },

  // Role Filter
  role: {
    name: "role_id",
    label: "Role",
    type: "select",
    placeholder: "All Roles",
    optionsKey: "roleOptions",
  },

  // Multiple Roles Filter
  roles: {
    name: "role_ids",
    label: "Roles",
    type: "multiselect",
    optionsKey: "roleOptions",
  },

  // Branch Filter
  branch: {
    name: "branch_id",
    label: "Branch",
    type: "select",
    placeholder: "All Branches",
    optionsKey: "branchOptions",
  },

  // Designation Filter
  designation: {
    name: "designation_id",
    label: "Designation",
    type: "select",
    placeholder: "All Designations",
    optionsKey: "designationOptions",
  },

  // Date Range Filter
  dateRange: {
    name: "date_range",
    label: "Date Range",
    type: "daterange",
  },

  // Created Date Range
  createdDateRange: {
    name: "created_date_range",
    label: "Created Date Range",
    type: "daterange",
  },

  // Single Date Filter
  date: {
    name: "date",
    label: "Date",
    type: "date",
  },

  // From Date
  fromDate: {
    name: "from_date",
    label: "From Date",
    type: "date",
  },

  // To Date
  toDate: {
    name: "to_date",
    label: "To Date",
    type: "date",
  },

  // Employment Type
  employmentType: {
    name: "employment_type",
    label: "Employment Type",
    type: "radio",
    options: [
      { label: "All", value: "" },
      { label: "Full Time", value: "full_time" },
      { label: "Part Time", value: "part_time" },
      { label: "Contract", value: "contract" },
    ],
  },

  // Gender Filter
  gender: {
    name: "gender",
    label: "Gender",
    type: "radio",
    options: [
      { label: "All", value: "" },
      { label: "Male", value: "male" },
      { label: "Female", value: "female" },
      { label: "Other", value: "other" },
    ],
  },

  // System Generated Toggle
  systemGenerated: {
    name: "is_system_generated",
    label: "System Generated Only",
    type: "toggle",
  },

  // Show Inactive Toggle
  showInactive: {
    name: "show_inactive",
    label: "Show Inactive",
    type: "toggle",
  },

  // Include Archived
  includeArchived: {
    name: "include_archived",
    label: "Include Archived",
    type: "checkbox",
  },

  // Verified Status
  verified: {
    name: "verified",
    label: "Verified Only",
    type: "checkbox",
  },

  // Priority Filter
  priority: {
    name: "priority",
    label: "Priority",
    type: "select",
    placeholder: "All Priorities",
    options: [
      { label: "Low", value: "low" },
      { label: "Medium", value: "medium" },
      { label: "High", value: "high" },
      { label: "Urgent", value: "urgent" },
    ],
  },

  // Category Filter (Generic)
  category: {
    name: "category_id",
    label: "Category",
    type: "select",
    placeholder: "All Categories",
    optionsKey: "categoryOptions",
  },

  // Vendor Filter
  vendor: {
    name: "vendor_id",
    label: "Vendor",
    type: "select",
    placeholder: "All Vendors",
    optionsKey: "vendorOptions",
  },

  // Staff Filter
  staff: {
    name: "staff_id",
    label: "Staff",
    type: "select",
    placeholder: "All Staff",
    optionsKey: "staffOptions",
  },

  // Customer Filter
  customer: {
    name: "customer_id",
    label: "Customer",
    type: "select",
    placeholder: "All Customers",
    optionsKey: "customerOptions",
  },

  // Lead Source
  leadSource: {
    name: "lead_source",
    label: "Lead Source",
    type: "select",
    placeholder: "All Sources",
    optionsKey: "leadSourceOptions",
  },

  // Lead Status
  leadStatus: {
    name: "lead_status",
    label: "Lead Status",
    type: "select",
    placeholder: "All Status",
    optionsKey: "leadStatusOptions",
  },

  // Amount Range
  amountRange: {
    name: "amount_range",
    label: "Amount Range",
    type: "daterange", // Will be used for min/max amounts
  },

  // Min Amount
  minAmount: {
    name: "min_amount",
    label: "Minimum Amount",
    type: "number",
    min: 0,
    placeholder: "Enter minimum amount",
  },

  // Max Amount
  maxAmount: {
    name: "max_amount",
    label: "Maximum Amount",
    type: "number",
    min: 0,
    placeholder: "Enter maximum amount",
  },

  // Search by Name
  searchName: {
    name: "search_name",
    label: "Search by Name",
    type: "text",
    placeholder: "Enter name...",
  },

  // Search by Email
  searchEmail: {
    name: "search_email",
    label: "Search by Email",
    type: "text",
    placeholder: "Enter email...",
  },

  // Search by Phone
  searchPhone: {
    name: "search_phone",
    label: "Search by Phone",
    type: "text",
    placeholder: "Enter phone number...",
  },

  // Year Filter
  year: {
    name: "year",
    label: "Year",
    type: "select",
    placeholder: "Select Year",
    optionsKey: "yearOptions",
  },

  // Month Filter
  month: {
    name: "month",
    label: "Month",
    type: "select",
    placeholder: "Select Month",
    options: [
      { label: "January", value: "1" },
      { label: "February", value: "2" },
      { label: "March", value: "3" },
      { label: "April", value: "4" },
      { label: "May", value: "5" },
      { label: "June", value: "6" },
      { label: "July", value: "7" },
      { label: "August", value: "8" },
      { label: "September", value: "9" },
      { label: "October", value: "10" },
      { label: "November", value: "11" },
      { label: "December", value: "12" },
    ],
  },
};

/**
 * Get filter configuration by names
 * 
 * @param {Array<string>} filterNames - Array of filter names to include
 * @param {Object} options - Options object containing dynamic data (e.g., departmentOptions)
 * @param {Object} overrides - Override default configurations for specific filters
 * 
 * @returns {Array} Array of filter field configurations
 * 
 * @example
 * // Basic usage
 * const filters = getFilterConfig(['department', 'status'], {
 *   departmentOptions: departmentList
 * });
 * 
 * @example
 * // With overrides
 * const filters = getFilterConfig(
 *   ['department', 'status'],
 *   { departmentOptions: departmentList },
 *   { status: { type: 'select' } } // Override status to use select instead of radio
 * );
 */
export const getFilterConfig = (filterNames = [], options = {}, overrides = {}) => {
  return filterNames.map((filterName) => {
    const baseConfig = FILTER_CONFIGS[filterName];
    
    if (!baseConfig) {
      console.warn(`Filter configuration not found for: ${filterName}`);
      return null;
    }

    // Clone the base config
    const config = { ...baseConfig };

    // If this filter requires dynamic options, get them from the options object
    if (config.optionsKey && options[config.optionsKey]) {
      config.options = options[config.optionsKey];
      delete config.optionsKey; // Remove the key as it's not needed in final config
    }

    // Apply any overrides for this filter
    if (overrides[filterName]) {
      Object.assign(config, overrides[filterName]);
    }

    return config;
  }).filter(Boolean); // Remove any null entries
};

/**
 * Get a single filter configuration
 * 
 * @param {string} filterName - Name of the filter
 * @param {Object} options - Options for the filter (e.g., options array)
 * @param {Object} override - Override default configuration
 * 
 * @returns {Object} Filter configuration object
 * 
 * @example
 * const departmentFilter = getSingleFilterConfig('department', {
 *   departmentOptions: departmentList
 * });
 */
export const getSingleFilterConfig = (filterName, options = {}, override = {}) => {
  const configs = getFilterConfig([filterName], options, { [filterName]: override });
  return configs[0] || null;
};

/**
 * Create a custom filter configuration
 * This is useful when you need a filter that's not in the predefined list
 * 
 * @param {Object} config - Custom filter configuration
 * @returns {Object} Filter configuration
 * 
 * @example
 * const customFilter = createCustomFilter({
 *   name: "custom_field",
 *   label: "Custom Field",
 *   type: "select",
 *   options: myCustomOptions
 * });
 */
export const createCustomFilter = (config) => {
  return {
    type: "text",
    placeholder: "Enter value...",
    ...config,
  };
};

/**
 * Commonly used filter combinations
 * Pre-defined sets of filters for common use cases
 */
export const FILTER_PRESETS = {
  // Basic filters for most listing pages
  basic: ['status'],
  
  // Staff management filters
  staff: ['department', 'designation', 'branch', 'status'],
  
  // Date-based filters
  dateFilters: ['dateRange'],
  
  // HR filters
  hr: ['department', 'designation', 'employmentType', 'status'],
  
  // Lead management filters
  leads: ['leadSource', 'leadStatus', 'dateRange'],
  
  // Inventory filters
  inventory: ['category', 'vendor', 'status'],
  
  // Financial filters
  financial: ['dateRange', 'minAmount', 'maxAmount', 'status'],
};

/**
 * Get preset filter configuration
 * 
 * @param {string} presetName - Name of the preset
 * @param {Object} options - Options for dynamic filters
 * @param {Object} overrides - Override configurations
 * 
 * @returns {Array} Array of filter configurations
 * 
 * @example
 * const filters = getPresetFilters('staff', {
 *   departmentOptions: departmentList,
 *   designationOptions: designationList,
 *   branchOptions: branchList
 * });
 */
export const getPresetFilters = (presetName, options = {}, overrides = {}) => {
  const preset = FILTER_PRESETS[presetName];
  
  if (!preset) {
    console.warn(`Filter preset not found: ${presetName}`);
    return [];
  }
  
  return getFilterConfig(preset, options, overrides);
};

export default {
  FILTER_CONFIGS,
  getFilterConfig,
  getSingleFilterConfig,
  createCustomFilter,
  FILTER_PRESETS,
  getPresetFilters,
};

